import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  BookOpen, FileText, TrendingUp, CheckCircle, Shield, CreditCard,
  ArrowRight, Download, AlertTriangle, Star, FileSpreadsheet,
  ChevronUp, ChevronDown, Printer, LayoutDashboard, Search, Folder,
  Package, Activity, Settings, ExternalLink, Tag, Cpu, RefreshCw,
  Sparkles, Loader2, Camera,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Primitives ────────────────────────────────────────────────────────────────

const Step = ({ num, text }) => (
  <div className="flex items-start gap-3 py-1">
    <span className="w-6 h-6 rounded-full bg-[#007934] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{num}</span>
    <p className="text-gray-700 text-sm leading-relaxed">{text}</p>
  </div>
);

const Note = ({ children, type = "info" }) => {
  const s = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    tip:  "bg-green-50 border-green-200 text-green-800",
  };
  return <div className={`border rounded-lg px-4 py-3 text-sm leading-relaxed ${s[type]}`}>{children}</div>;
};

const SectionBlock = ({ num, title, icon: Icon, iconColor = "text-[#007934]", children }) => (
  <div className="border-b border-gray-100 pb-7 mb-7 last:border-0 last:mb-0 last:pb-0">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon size={16} className={iconColor} />
      </div>
      <h3 className="font-bold text-gray-900 text-base font-['Outfit']">
        {num && <span className="text-gray-400 font-normal mr-1">{num}.</span>}{title}
      </h3>
    </div>
    <div className="ml-11 space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
  </div>
);

// ── PDF builder — builds clean standalone HTML (no Tailwind needed) ───────────

function buildPDFHtml(type) {
  const isOffers = type === "offers";
  const title = isOffers ? "Manual del Responsable de Ofertas" : "Manual del Director Financiero (CFO)";
  const subtitle = isOffers
    ? "Guía para crear, gestionar y tramitar ofertas TIC en Valoriza Plataformas Corporativas"
    : "Guía para revisar y aprobar ofertas TIC en Valoriza Plataformas Corporativas";
  const date = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
  const accentColor = isOffers ? "#0d7377" : "#007934";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Inter', Arial, sans-serif; font-size: 10.5pt; color: #1e1e1e; background: white; }
    .page { max-width: 800px; margin: 0 auto; padding: 60px 50px; }
    h1,h2,h3,h4 { font-family: 'Outfit', Arial, sans-serif; }

    /* Cover */
    .cover { border-left: 6px solid ${accentColor}; padding: 28px 28px 28px 28px; margin-bottom: 40px; background: #f8fafb; border-radius: 0 8px 8px 0; }
    .cover h1 { font-size: 22pt; color: ${accentColor}; margin-bottom: 6px; font-weight: 800; }
    .cover p { color: #555; font-size: 10pt; margin-bottom: 4px; }
    .cover .meta { font-size: 8.5pt; color: #999; margin-top: 10px; border-top: 1px solid #e0e0e0; padding-top: 8px; }

    /* TOC */
    .toc { background: #f9f9f9; border: 1px solid #eee; border-radius: 8px; padding: 16px 20px; margin-bottom: 36px; }
    .toc h4 { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.08em; color: #888; margin-bottom: 10px; }
    .toc-item { font-size: 9.5pt; color: #444; padding: 2px 0; }
    .toc-item span { color: ${accentColor}; font-weight: 600; margin-right: 6px; }

    /* Section */
    .section { border-bottom: 1px solid #efefef; padding-bottom: 24px; margin-bottom: 24px; }
    .section:last-child { border-bottom: none; }
    .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
    .section-num { width: 28px; height: 28px; background: ${accentColor}; color: white; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 9pt; font-weight: 700; flex-shrink: 0; }
    .section h3 { font-size: 12.5pt; color: #111; font-weight: 700; }
    .body { margin-left: 38px; }

    /* Steps */
    .step { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
    .step-num { width: 22px; height: 22px; background: ${accentColor}; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 8pt; font-weight: 700; flex-shrink: 0; }
    .step p { font-size: 10pt; color: #333; line-height: 1.55; padding-top: 1px; }

    /* Notes */
    .note { border-radius: 6px; padding: 10px 14px; margin: 10px 0; font-size: 9.5pt; line-height: 1.5; }
    .note-info  { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
    .note-warn  { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
    .note-tip   { background: #f0fdf4; border: 1px solid #bbf7d0; color: #14532d; }

    /* Lists */
    ul { padding-left: 16px; }
    ul li { margin-bottom: 5px; font-size: 10pt; color: #333; line-height: 1.5; }
    strong { font-weight: 600; color: ${accentColor}; }
    p { margin-bottom: 8px; font-size: 10pt; line-height: 1.6; color: #333; }

    /* Alert badge */
    .badge-alerta { display: inline-flex; align-items: center; gap: 4px; font-size: 8pt; font-weight: 700; color: #92400e; background: #fef3c7; border: 1px solid #fde68a; padding: 2px 8px; border-radius: 999px; }

    /* Highlight box */
    .highlight { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 10px 14px; font-size: 9.5pt; line-height: 1.55; }
    .highlight li { margin-bottom: 4px; }

    /* Flow row */
    .flow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
    .flow-step { font-size: 8.5pt; background: #f3f4f6; color: #374151; padding: 4px 10px; border-radius: 999px; font-weight: 500; }
    .flow-arr { color: #9ca3af; font-size: 10pt; }

    /* Footer */
    .footer { margin-top: 40px; border-top: 2px solid ${accentColor}; padding-top: 14px; text-align: center; }
    .footer p { font-size: 8pt; color: #aaa; }

    @media print {
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { padding: 0; }
      .page { padding: 30px 40px; }
      .section { page-break-inside: avoid; }
    }
  `;

  // ── Content builders ──
  const step = (n, t) => `<div class="step"><div class="step-num">${n}</div><p>${t}</p></div>`;
  const note = (t, cls) => `<div class="note note-${cls}">${t}</div>`;
  const secOpen = (numStr, title) =>
    `<div class="section"><div class="section-header"><div class="section-num">${numStr}</div><h3>${title}</h3></div><div class="body">`;
  const secClose = () => `</div></div>`;

  let body = "";

  if (isOffers) {
    body = `
      ${secOpen("1", "Acceso y Navegación")}
        ${step(1, "Accede a <strong>https://plataformascorporativas.valorizasm.com</strong> con tu email y contraseña.")}
        ${step(2, "En el menú lateral, busca <strong>'Ofertas TIC'</strong> para ver el listado de todas las ofertas.")}
        ${step(3, "Desde ahí puedes crear nuevas ofertas, ver el estado de las existentes y acceder al detalle de cada una.")}
        ${note("Tu rol <strong>Responsable de Oferta</strong> te permite crear y editar ofertas, pero la aprobación final corresponde al equipo TIC y al CFO.", "info")}
      ${secClose()}

      ${secOpen("2", "Crear una Nueva Oferta")}
        <p>Hay dos formas de crear una oferta: manualmente o importando desde un Excel.</p>
        <p><strong>Opción A — Manual:</strong></p>
        ${step(1, "En 'Ofertas TIC', pulsa el botón <strong>'+ Nueva Oferta'</strong> (esquina superior derecha).")}
        ${step(2, "Rellena: Título, Licitación/Expediente, Municipio, Tipo de contrato, Deadline.")}
        ${step(3, "Indica el Responsable de Negocio y su email de contacto.")}
        ${step(4, "Pulsa <strong>'Crear oferta'</strong>. Se crea en estado Borrador.")}
        ${note("El título debe ser descriptivo: incluye tipo de servicio y municipio. Ej: <em>'Sistema RSU MOVISAT — Nerja'</em>.", "tip")}
        <p style="margin-top:10px"><strong>Opción B — Desde Excel (Importación Pando):</strong></p>
        ${step(1, "Pulsa el botón verde <strong>'Desde Excel'</strong> (junto a + Nueva Oferta).")}
        ${step(2, "Arrastra o selecciona tu fichero .xlsx o .xls (Alcance IT, pliego municipal, cuadro de precios…).")}
        ${step(3, "Opcionalmente introduce título y municipio; la Pando los detectará del Excel si los omites.")}
        ${step(4, "Pulsa <strong>'Crear oferta con Pando'</strong> y espera 20-40 segundos.")}
        ${step(5, "La oferta se crea con las secciones CAPEX/OPEX ya rellenas automáticamente.")}
      ${secClose()}

      ${secOpen("3", "Añadir Partidas CAPEX y OPEX")}
        <p>En el bloque <strong>'Desglose CAPEX / OPEX'</strong> del detalle de la oferta:</p>
        ${step(1, "Pulsa <strong>'+ CAPEX'</strong> para añadir una sección de inversión única (hardware, software, instalaciones).")}
        ${step(2, "Pulsa <strong>'+ OPEX'</strong> para añadir una sección de gastos anuales recurrentes (SaaS, mantenimientos, comunicaciones).")}
        ${step(3, "Dentro de cada sección, pulsa <strong>'+ Añadir concepto'</strong> para detallar cada partida: descripción, cantidad y precio unitario.")}
        ${step(4, "Los totales se calculan automáticamente. Para CAPEX, puedes ajustar los años de amortización.")}
        ${note("<strong>Guarda los cambios</strong> pulsando fuera de los campos de texto o usando el botón de guardar.", "warn")}
        <div class="highlight" style="margin-top:10px">
          <p style="font-weight:600;margin-bottom:6px">Ejemplos de partidas habituales:</p>
          <ul>
            <li><strong>CAPEX:</strong> GPS embarcados, servidores, licencias base, RFID, sensores IoT, cámaras LPR</li>
            <li><strong>OPEX:</strong> Mantenimiento anual HW, SaaS/cloud, comunicaciones M2M, soporte CAU</li>
          </ul>
        </div>
      ${secClose()}

      ${secOpen("3b", "Importar CAPEX/OPEX desde Excel")}
        <p>Si tienes un Excel del Alcance IT o pliego de precios, puedes importarlo directamente desde el detalle de la oferta:</p>
        ${step(1, "En el bloque 'Desglose CAPEX / OPEX', pulsa el botón <strong>'Importar Excel'</strong>.")}
        ${step(2, "Selecciona el fichero .xlsx o .xls.")}
        ${step(3, "Pulsa <strong>'Importar con Pando'</strong> y espera 20-40 segundos.")}
        ${step(4, "Las secciones actuales se reemplazan con las partidas detectadas por la Pando.")}
        ${note("La importación <strong>reemplaza todas las partidas existentes</strong>. Asegúrate de que no hay datos que quieras conservar antes de importar.", "warn")}
        <div class="note note-tip" style="margin-top:8px">
          Los ítems con alcance o precio sin definir en el Excel se marcarán automáticamente con el badge <span class="badge-alerta">⚠ ALERTA</span> para que los revises antes de solicitar validación.
        </div>
      ${secClose()}

      ${secOpen("4", "Análisis Pando de la Oferta")}
        ${step(1, "En el detalle de la oferta, localiza la sección <strong>'Análisis Pando · Valoración Técnico-Económica'</strong>.")}
        ${step(2, "Pulsa <strong>'Generar análisis Pando'</strong> (requiere que hayas añadido partidas CAPEX/OPEX).")}
        ${step(3, "Espera 15-30 segundos mientras la Pando procesa la información.")}
        ${step(4, "Revisa el análisis: resumen ejecutivo, valoración económica, puntos fuertes, riesgos y recomendaciones.")}
        ${step(5, "Pulsa <strong>'Imprimir PDF'</strong> para generar un PDF del informe Pando.")}
        ${note("Usa el análisis Pando para justificar los importes ante el TIC y el CFO. Puedes regenerarlo si actualizas las partidas.", "tip")}
      ${secClose()}

      ${secOpen("5", "Solicitar Validación TIC")}
        <p>Cuando la oferta esté completa:</p>
        ${step(1, "Pulsa el botón <strong>'Solicitar validación TIC'</strong> en el panel de estado.")}
        ${step(2, "El sistema enviará un email automático al equipo TIC con el resumen y el análisis Pando.")}
        ${step(3, "El estado cambia a <strong>'Pendiente validación TIC'</strong>. Ya no podrás editar la oferta.")}
        ${note("Asegúrate de que <strong>todas las partidas están correctas</strong> ANTES de solicitar la validación. No podrás editar la oferta una vez enviada.", "warn")}
      ${secClose()}

      ${secOpen("6", "Seguimiento del Estado")}
        <p>El flujo de aprobación sigue este orden:</p>
        <div class="flow">
          <span class="flow-step">Borrador</span><span class="flow-arr">→</span>
          <span class="flow-step">Validación TIC</span><span class="flow-arr">→</span>
          <span class="flow-step">Aprobación CFO</span><span class="flow-arr">→</span>
          <span class="flow-step">Aprobada</span>
        </div>
        <ul style="margin-top:10px">
          <li>Si el TIC <strong>rechaza</strong>: recibirás un email con los motivos. Deberás corregir y volver a solicitar.</li>
          <li>Si el TIC <strong>aprueba</strong>: pasa al CFO. Recibirás notificación cuando haya decisión.</li>
          <li>Si el CFO <strong>aprueba</strong>: la oferta queda en estado 'Aprobada CFO'. Se notifica a todos los implicados.</li>
        </ul>
      ${secClose()}

      ${secOpen("7", "Desestimar una Oferta")}
        ${step(1, "En el listado de ofertas, accede al detalle de la oferta que quieras desestimar.")}
        ${step(2, "Usa la opción <strong>'Desestimar'</strong> e indica el motivo (ej: 'No adjudicada', 'Cancelada por el municipio').")}
        ${note("Una oferta desestimada queda archivada y no se puede volver a activar.", "info")}
      ${secClose()}

      ${secOpen("8", "Buenas Prácticas")}
        <ul>
          <li>Añade siempre una descripción técnica detallada para facilitar la validación TIC.</li>
          <li>Genera el análisis Pando <strong>antes</strong> de solicitar validación. Ayuda al validador a entender la propuesta.</li>
          <li>Organiza las partidas por categorías claras: Software, Hardware, Instalaciones, Mantenimiento.</li>
          <li>Indica el deadline correcto para que el equipo pueda priorizar.</li>
          <li>Para contratos plurianuales, usa el multiplicador de años en secciones CAPEX.</li>
          <li>Revisa todas las partidas con badge <span class="badge-alerta">⚠ ALERTA</span> antes de enviar a validación.</li>
        </ul>
      ${secClose()}
    `;
  } else {
    body = `
      ${secOpen("1", "Acceso a la Plataforma")}
        ${step(1, "Accede a <strong>https://plataformascorporativas.valorizasm.com</strong> con tu email y contraseña.")}
        ${step(2, "Si aún no tienes cuenta, contacta con el administrador TIC (dredondo@valorizasm.com).")}
        ${step(3, "El menú lateral mostrará los módulos disponibles según tu rol.")}
        ${note("Como <strong>CFO</strong>, tienes acceso a: Ofertas TIC, Dashboard Ejecutivo, Presupuesto y Reportes.", "tip")}
      ${secClose()}

      ${secOpen("2", "Identificar Ofertas Pendientes")}
        <p>Recibirás un <strong>email automático</strong> cuando el equipo TIC haya validado una oferta y requiera tu aprobación final.</p>
        ${step(1, "En el menú lateral, haz clic en <strong>'Ofertas TIC'</strong>.")}
        ${step(2, "Identifica las tarjetas con el badge azul <strong>'Pendiente aprobación CFO'</strong>.")}
        ${step(3, "Haz clic en la oferta para ver el detalle completo.")}
        ${note("Las ofertas que esperan tu decisión tienen estado en azul: <em>Pendiente aprobación CFO</em>.", "info")}
      ${secClose()}

      ${secOpen("3", "Estructura de una Oferta TIC")}
        <p>Cada oferta contiene las siguientes secciones clave que debes revisar:</p>
        <ul>
          <li><strong>CAPEX:</strong> Inversión única en hardware, software, licencias e instalaciones. Se amortiza durante el contrato.</li>
          <li><strong>OPEX:</strong> Gasto operativo anual recurrente: mantenimientos, licencias SaaS, comunicaciones, hosting.</li>
          <li><strong>TOTAL TIC:</strong> Suma de CAPEX + OPEX anual. Refleja el coste total del primer año.</li>
        </ul>
        ${note("El desglose por partidas (Software, Hardware, Instalaciones…) está disponible desplazándote hacia abajo en la oferta.", "tip")}
      ${secClose()}

      ${secOpen("4", "Interpretar Alertas y Partidas Pendientes")}
        <p>Al revisar el bloque <strong>'Desglose CAPEX / OPEX'</strong>, presta atención a:</p>
        <ul>
          <li>Badge <span class="badge-alerta">⚠ ALERTA</span>: Partida con alcance o precio sin definir. Puede implicar coste oculto — consulta con el responsable TIC antes de aprobar.</li>
          <li><strong>0 €</strong> en precio unitario: Coste pendiente de confirmación con proveedor.</li>
        </ul>
        ${note("Los ítems a 0 € <strong>no se incluyen</strong> en el TOTAL TIC mostrado. El total real podría ser mayor cuando se confirmen esos importes.", "warn")}
        ${note("Es habitual ver alertas en partidas de 'Integración Smart City' u 'OCR de vehículos' cuyo alcance depende del pliego municipal. Pregunta al TIC si no está claro.", "info")}
      ${secClose()}

      ${secOpen("5", "Consultar el Análisis Pando")}
        ${step(1, "En el detalle de la oferta, localiza la sección <strong>'Análisis Pando · Valoración Técnico-Económica'</strong>.")}
        ${step(2, "Revisa el <strong>Resumen Ejecutivo</strong> y la <strong>Valoración Económica</strong> (Adecuado / Elevado / Muy elevado).")}
        ${step(3, "Consulta los <strong>Puntos Fuertes</strong>, <strong>Riesgos</strong> y la <strong>Conclusión</strong>.")}
        ${note("Si el análisis no está generado, el responsable TIC debe pulsar 'Generar análisis Pando' antes de enviarla a tu aprobación.", "info")}
      ${secClose()}

      ${secOpen("6", "Proceso de Aprobación CFO")}
        <p>El flujo de aprobación es:</p>
        <div class="flow">
          <span class="flow-step">Borrador</span><span class="flow-arr">→</span>
          <span class="flow-step">Validación TIC</span><span class="flow-arr">→</span>
          <span class="flow-step"><strong>Aprobación CFO</strong></span><span class="flow-arr">→</span>
          <span class="flow-step">Aprobada</span>
        </div>
        ${step(1, "Comprueba que el estado es <strong>'Pendiente aprobación CFO'</strong>.")}
        ${step(2, "Revisa CAPEX, OPEX, análisis Pando y comentarios del equipo TIC.")}
        ${step(3, "Haz clic en el botón azul <strong>'Aprobación CFO'</strong> en el panel de estado.")}
        ${step(4, "Selecciona <strong>'Aprobar oferta'</strong> o <strong>'Rechazar'</strong> y añade comentarios (obligatorio si rechazas).")}
        ${step(5, "Pulsa <strong>'Confirmar decisión CFO'</strong>.")}
        ${note("Al rechazar, el responsable de oferta recibirá un email con el motivo. Al aprobar, se notifica a todo el equipo.", "warn")}
      ${secClose()}

      ${secOpen("7", "Dashboard y Presupuesto")}
        <ul>
          <li><strong>Dashboard:</strong> KPIs globales del portafolio: proyectos activos, % presupuesto ejecutado, items pendientes.</li>
          <li><strong>Presupuesto:</strong> Desglose del presupuesto TIC anual por categorías. Permite ajustar límites.</li>
          <li><strong>Informes:</strong> Generación de informes ejecutivos en PDF con el estado del portafolio.</li>
        </ul>
      ${secClose()}

      ${secOpen("8", "Historial de Aprobaciones")}
        <p>En cada oferta encontrarás un <strong>Historial de aprobaciones</strong> al final del panel de estado, con:</p>
        <ul>
          <li>Quién solicitó la validación TIC y cuándo</li>
          <li>Decisión del equipo TIC y comentarios</li>
          <li>Tu decisión como CFO</li>
        </ul>
        ${note("Este historial queda registrado permanentemente para auditoría.", "tip")}
      ${secClose()}
    `;
  }

  const toc = isOffers
    ? ["Acceso y Navegación", "Crear una Nueva Oferta", "Añadir CAPEX y OPEX", "Importar desde Excel", "Análisis Pando", "Solicitar Validación TIC", "Seguimiento del Estado", "Desestimar", "Buenas Prácticas"]
    : ["Acceso a la Plataforma", "Identificar Ofertas Pendientes", "Estructura de una Oferta", "Alertas y Partidas Pendientes", "Consultar Análisis Pando", "Proceso de Aprobación CFO", "Dashboard y Presupuesto", "Historial de Aprobaciones"];

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title} — Valoriza</title>
  <style>${css}</style>
</head>
<body>
<div class="page">
  <div class="cover">
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px">
      <div style="width:40px;height:40px;background:${accentColor};border-radius:8px;display:flex;align-items:center;justify-content:center">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
      </div>
      <div>
        <div style="font-size:8pt;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:2px">Valoriza Plataformas Corporativas</div>
        <h1>${title}</h1>
      </div>
    </div>
    <p>${subtitle}</p>
    <div class="meta">Documento generado el ${date} · Para uso interno · Gestión de Demanda IT</div>
  </div>

  <div class="toc">
    <h4>Índice de contenidos</h4>
    ${toc.map((t, i) => `<div class="toc-item"><span>${i + 1}.</span>${t}</div>`).join("")}
  </div>

  ${body}

  <div class="footer">
    <p>Valoriza Plataformas Corporativas · Gestión de Demanda IT · Confidencial — uso interno</p>
    <p style="margin-top:4px">Soporte técnico: dredondo@valorizasm.com</p>
  </div>
</div>
<script>
  window.onload = function() {
    setTimeout(function() { window.print(); }, 600);
  };
</script>
</body>
</html>`;
}

// ── In-app section renderer ──────────────────────────────────────────────────

function ManualSection({ num, title, icon: Icon, iconColor = "text-[#007934]", children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon size={14} className={iconColor} />
          </div>
          <span className="font-semibold text-gray-900 text-sm font-['Outfit']">
            {num && <span className="text-gray-400 font-normal">{num}. </span>}{title}
          </span>
        </div>
        {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-2 bg-white border-t border-gray-50 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}

// ── CFO MANUAL ───────────────────────────────────────────────────────────────

function CfoManual() {
  return (
    <div className="space-y-2">
      <div className="bg-gradient-to-br from-[#007934] to-[#4CAF50] rounded-xl p-5 text-white mb-4">
        <div className="flex items-center gap-3 mb-1">
          <CreditCard size={20} />
          <h2 className="text-lg font-bold font-['Outfit']">Manual del Director Financiero (CFO)</h2>
        </div>
        <p className="text-green-100 text-sm">Guía para revisar y aprobar ofertas TIC en Valoriza Plataformas Corporativas</p>
      </div>

      <ManualSection num="1" title="Acceso a la Plataforma" icon={BookOpen}>
        <Step num="1" text="Accede a https://plataformascorporativas.valorizasm.com con tu email y contraseña." />
        <Step num="2" text="Si aún no tienes cuenta, contacta con el administrador TIC (dredondo@valorizasm.com)." />
        <Step num="3" text="El menú lateral mostrará los módulos disponibles según tu rol." />
        <Note type="tip">Como CFO tienes acceso a: Ofertas TIC, Dashboard Ejecutivo, Presupuesto y Reportes.</Note>
      </ManualSection>

      <ManualSection num="2" title="Identificar Ofertas Pendientes de Aprobación" icon={AlertTriangle} iconColor="text-amber-500">
        <p className="text-sm text-gray-600">Recibirás un <strong>email automático</strong> cuando el equipo TIC haya validado una oferta y requiera tu aprobación final.</p>
        <Step num="1" text="En el menú lateral, haz clic en 'Ofertas TIC'." />
        <Step num="2" text="Identifica las tarjetas con el badge azul 'Pendiente aprobación CFO'." />
        <Step num="3" text="Haz clic en la oferta para ver el detalle completo." />
        <Note type="info">Las ofertas que esperan tu decisión tienen estado en azul: <em>Pendiente aprobación CFO</em>.</Note>
      </ManualSection>

      <ManualSection num="3" title="Estructura de una Oferta TIC" icon={FileText}>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-start gap-2"><span className="text-[#007934] font-bold flex-shrink-0 w-14">CAPEX:</span><span>Inversión única en hardware, software, licencias e instalaciones. Se amortiza durante el contrato.</span></div>
          <div className="flex items-start gap-2"><span className="text-teal-700 font-bold flex-shrink-0 w-14">OPEX:</span><span>Gasto operativo anual recurrente: mantenimientos, licencias SaaS, comunicaciones, hosting.</span></div>
          <div className="flex items-start gap-2"><span className="font-bold flex-shrink-0 w-14">TOTAL:</span><span>Suma de CAPEX + OPEX anual. Refleja el coste total del primer año de contrato.</span></div>
        </div>
        <Note type="tip">El desglose por partidas está disponible desplazándote hacia abajo en el detalle de la oferta.</Note>
      </ManualSection>

      <ManualSection num="4" title="Interpretar Alertas y Partidas Pendientes" icon={FileSpreadsheet} iconColor="text-amber-600">
        <p className="text-sm text-gray-600">Al revisar el bloque <strong>Desglose CAPEX / OPEX</strong>, presta atención a:</p>
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm">
            <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full mt-0.5">⚠ ALERTA</span>
            <span className="text-gray-600">Alcance o precio sin definir. Puede implicar coste oculto — consulta con el TIC antes de aprobar.</span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span className="text-[#007934] font-bold flex-shrink-0 mt-0.5">0 €</span>
            <span className="text-gray-600">Precio pendiente de confirmación con proveedor.</span>
          </div>
        </div>
        <Note type="warn">Los ítems a 0 € <strong>no se incluyen</strong> en el TOTAL TIC. El coste real puede ser mayor cuando se confirmen.</Note>
        <Note type="info">Es habitual ver alertas en 'Integración Smart City' u 'OCR de vehículos' — dependen del pliego municipal.</Note>
      </ManualSection>

      <ManualSection num="5" title="Consultar el Análisis Pando" icon={Star} iconColor="text-purple-600">
        <Step num="1" text="En el detalle de la oferta, localiza la sección 'Análisis Pando · Valoración Técnico-Económica'." />
        <Step num="2" text="Revisa el Resumen Ejecutivo y la Valoración Económica (Adecuado / Elevado / Muy elevado)." />
        <Step num="3" text="Consulta los Puntos Fuertes, Riesgos y la Conclusión del análisis." />
        <Note type="info">Si el análisis no está generado, el responsable TIC debe pulsarlo antes de enviarte la oferta.</Note>
      </ManualSection>

      <ManualSection num="6" title="Proceso de Aprobación CFO" icon={CheckCircle} iconColor="text-green-600">
        <p className="text-sm text-gray-600">El flujo de aprobación es: <strong>Borrador → Validación TIC → Aprobación CFO → Aprobada</strong></p>
        <Step num="1" text="Comprueba que el estado es 'Pendiente aprobación CFO'." />
        <Step num="2" text="Revisa CAPEX, OPEX, análisis Pando y comentarios del equipo TIC." />
        <Step num="3" text="Haz clic en el botón azul 'Aprobación CFO' en el panel de estado." />
        <Step num="4" text="Selecciona 'Aprobar oferta' o 'Rechazar' y añade comentarios (obligatorio si rechazas)." />
        <Step num="5" text="Pulsa 'Confirmar decisión CFO'." />
        <Note type="warn">Al rechazar, el responsable recibirá un email con el motivo. Al aprobar, se notifica a todos.</Note>
      </ManualSection>

      <ManualSection num="7" title="Dashboard y Presupuesto" icon={TrendingUp}>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-start gap-2"><ArrowRight size={13} className="text-[#007934] mt-0.5 flex-shrink-0"/><span><strong>Dashboard:</strong> KPIs globales: proyectos activos, % presupuesto ejecutado, items pendientes.</span></div>
          <div className="flex items-start gap-2"><ArrowRight size={13} className="text-[#007934] mt-0.5 flex-shrink-0"/><span><strong>Presupuesto:</strong> Desglose del presupuesto TIC anual por categorías. Permite ajustar límites.</span></div>
          <div className="flex items-start gap-2"><ArrowRight size={13} className="text-[#007934] mt-0.5 flex-shrink-0"/><span><strong>Informes:</strong> Informes ejecutivos en PDF con el estado del portafolio.</span></div>
        </div>
      </ManualSection>

      <ManualSection num="8" title="Historial de Aprobaciones" icon={BookOpen}>
        <p className="text-sm text-gray-600">En cada oferta encontrarás un historial al final del panel de estado con:</p>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Quién solicitó la validación TIC y cuándo</li>
          <li>Decisión del equipo TIC y comentarios</li>
          <li>Tu decisión como CFO</li>
        </ul>
        <Note type="tip">Este historial queda registrado permanentemente para auditoría.</Note>
      </ManualSection>

      <Note type="tip"><strong>Soporte:</strong> Para incidencias técnicas, contacta con David Redondo (dredondo@valorizasm.com).</Note>
    </div>
  );
}

// ── OFFERS MANUAL ────────────────────────────────────────────────────────────

function OffersManual() {
  return (
    <div className="space-y-2">
      <div className="bg-gradient-to-br from-teal-700 to-teal-500 rounded-xl p-5 text-white mb-4">
        <div className="flex items-center gap-3 mb-1">
          <FileText size={20} />
          <h2 className="text-lg font-bold font-['Outfit']">Manual del Responsable de Ofertas</h2>
        </div>
        <p className="text-teal-100 text-sm">Guía para crear, gestionar y tramitar ofertas TIC en Valoriza Plataformas Corporativas</p>
      </div>

      <ManualSection num="1" title="Acceso y Navegación" icon={BookOpen}>
        <Step num="1" text="Accede a https://plataformascorporativas.valorizasm.com con tu email y contraseña." />
        <Step num="2" text="En el menú lateral, busca 'Ofertas TIC' para ver el listado de todas las ofertas." />
        <Step num="3" text="Desde ahí puedes crear nuevas ofertas, ver el estado y acceder al detalle de cada una." />
        <Note type="info">Tu rol 'Responsable de Oferta' te permite crear y editar ofertas, pero la aprobación final corresponde al equipo TIC y al CFO.</Note>
      </ManualSection>

      <ManualSection num="2" title="Crear una Nueva Oferta" icon={FileText} iconColor="text-teal-600">
        <p className="text-sm text-gray-700 font-semibold">Opción A — Manual:</p>
        <Step num="1" text="En 'Ofertas TIC', pulsa '+ Nueva Oferta' (esquina superior derecha)." />
        <Step num="2" text="Rellena: Título, Licitación, Municipio, Tipo de contrato, Deadline y Responsable de Negocio." />
        <Step num="3" text="Pulsa 'Crear oferta'. Se crea en estado Borrador." />
        <Note type="tip">El título debe ser descriptivo: tipo de servicio + municipio. Ej: <em>'Sistema RSU MOVISAT — Nerja'</em>.</Note>
        <p className="text-sm text-gray-700 font-semibold mt-2">Opción B — Desde Excel (Importación Pando):</p>
        <Step num="1" text="Pulsa el botón verde 'Desde Excel' (junto a + Nueva Oferta)." />
        <Step num="2" text="Selecciona tu fichero .xlsx — Alcance IT, pliego municipal, cuadro de precios…" />
        <Step num="3" text="Opcionalmente introduce título y municipio (la Pando los detecta del Excel si los omites)." />
        <Step num="4" text="Pulsa 'Crear oferta con Pando' y espera 20-40 segundos." />
        <Step num="5" text="La oferta se crea con las secciones CAPEX/OPEX ya rellenas automáticamente." />
      </ManualSection>

      <ManualSection num="3" title="Añadir Partidas CAPEX y OPEX" icon={TrendingUp}>
        <p className="text-sm text-gray-600">En el bloque <strong>'Desglose CAPEX / OPEX'</strong> del detalle de la oferta:</p>
        <Step num="1" text="Pulsa '+ CAPEX' para añadir una sección de inversión única (hardware, software, instalaciones)." />
        <Step num="2" text="Pulsa '+ OPEX' para añadir una sección de gastos anuales recurrentes (SaaS, mantenimientos, comunicaciones)." />
        <Step num="3" text="Dentro de cada sección, pulsa '+ Añadir concepto' con descripción, cantidad y precio unitario." />
        <Step num="4" text="Los totales se calculan automáticamente. Ajusta los años de amortización en CAPEX si procede." />
        <Note type="warn">Guarda los cambios pulsando fuera de los campos o con el botón de guardar.</Note>
        <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
          <p className="font-semibold text-gray-700">Ejemplos habituales:</p>
          <p><strong>CAPEX:</strong> GPS embarcados, servidores, licencias base, RFID, sensores IoT, cámaras LPR</p>
          <p><strong>OPEX:</strong> Mantenimiento anual HW, SaaS/cloud, comunicaciones M2M, soporte CAU</p>
        </div>
      </ManualSection>

      <ManualSection num="3b" title="Importar CAPEX/OPEX desde Excel" icon={FileSpreadsheet} iconColor="text-teal-600">
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-xs space-y-1 mb-2">
          <p className="font-semibold text-teal-800">Formatos aceptados:</p>
          <p className="text-teal-700">· Alcance IT de Valoriza · Pliegos municipales · Presupuestos de proveedor · Cualquier Excel con columnas de concepto/unidades/precio</p>
        </div>
        <p className="text-sm text-gray-700 font-semibold">Desde el detalle de una oferta existente:</p>
        <Step num="1" text="En el bloque CAPEX/OPEX, pulsa 'Importar Excel'." />
        <Step num="2" text="Selecciona el fichero y pulsa 'Importar con Pando'." />
        <Step num="3" text="Las secciones actuales se reemplazan con las partidas detectadas." />
        <Note type="warn">La importación <strong>reemplaza todas las partidas existentes</strong>. Guarda tus datos antes si los necesitas.</Note>
        <Note type="tip">Los ítems con alcance/precio sin definir en el Excel se marcan automáticamente con badge <strong>ALERTA</strong> para revisión.</Note>
      </ManualSection>

      <ManualSection num="4" title="Análisis Pando de la Oferta" icon={Star} iconColor="text-purple-600">
        <Step num="1" text="En el detalle, localiza 'Análisis Pando · Valoración Técnico-Económica'." />
        <Step num="2" text="Pulsa 'Generar análisis Pando' (requiere tener partidas CAPEX/OPEX añadidas)." />
        <Step num="3" text="Espera 15-30 segundos. Revisa: resumen, valoración económica, puntos fuertes, riesgos." />
        <Step num="4" text="Pulsa 'Imprimir PDF' para exportar el informe Pando." />
        <Note type="tip">Regenera el análisis si actualizas las partidas antes de enviar a validación.</Note>
      </ManualSection>

      <ManualSection num="5" title="Solicitar Validación TIC" icon={Shield} iconColor="text-amber-600">
        <Step num="1" text="Pulsa el botón naranja 'Solicitar validación TIC' en el panel de estado." />
        <Step num="2" text="El sistema enviará un email automático al equipo TIC con el resumen y el análisis Pando." />
        <Step num="3" text="El estado cambia a 'Pendiente validación TIC'. Ya no podrás editar la oferta." />
        <Note type="warn">Asegúrate de que todas las partidas estén correctas ANTES. No podrás editar una vez enviada.</Note>
      </ManualSection>

      <ManualSection num="6" title="Seguimiento del Estado" icon={CheckCircle} iconColor="text-green-600">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {["Borrador", "Validación TIC", "Aprobación CFO", "Aprobada"].map((s, i, arr) => (
            <React.Fragment key={s}>
              <span className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full text-xs font-medium">{s}</span>
              {i < arr.length - 1 && <ArrowRight size={12} className="text-gray-400" />}
            </React.Fragment>
          ))}
        </div>
        <div className="space-y-1.5 mt-2 text-sm text-gray-600">
          <div className="flex gap-2"><ArrowRight size={13} className="text-red-400 flex-shrink-0 mt-0.5"/><span>TIC <strong>rechaza</strong>: recibirás email con motivos. Corrige y vuelve a solicitar.</span></div>
          <div className="flex gap-2"><ArrowRight size={13} className="text-blue-400 flex-shrink-0 mt-0.5"/><span>TIC <strong>aprueba</strong>: pasa al CFO. Recibirás notificación cuando haya decisión.</span></div>
          <div className="flex gap-2"><ArrowRight size={13} className="text-[#007934] flex-shrink-0 mt-0.5"/><span>CFO <strong>aprueba</strong>: oferta en estado 'Aprobada CFO'. Se notifica a todos.</span></div>
        </div>
      </ManualSection>

      <ManualSection num="7" title="Desestimar una Oferta" icon={AlertTriangle} iconColor="text-red-500">
        <Step num="1" text="En el listado de ofertas, accede al detalle de la oferta." />
        <Step num="2" text="Usa la opción 'Desestimar' e indica el motivo (ej: 'No adjudicada', 'Cancelada por el municipio')." />
        <Note type="info">Una oferta desestimada queda archivada y no se puede volver a activar.</Note>
      </ManualSection>

      <ManualSection num="8" title="Buenas Prácticas" icon={Star} iconColor="text-amber-500">
        <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside">
          <li>Añade una descripción técnica detallada para facilitar la validación TIC.</li>
          <li>Genera el análisis Pando <strong>antes</strong> de solicitar validación.</li>
          <li>Organiza las partidas por categorías claras: Software, Hardware, Instalaciones, Mantenimiento.</li>
          <li>Indica el deadline correcto para que el equipo pueda priorizar revisiones.</li>
          <li>Para contratos plurianuales, usa el multiplicador de años en secciones CAPEX.</li>
          <li>Revisa todas las partidas con badge ALERTA antes de enviar a validación.</li>
        </ul>
      </ManualSection>

      <Note type="tip"><strong>Soporte:</strong> Para incidencias técnicas, contacta con David Redondo (dredondo@valorizasm.com).</Note>
    </div>
  );
}

// ── PLATFORM MANUAL — Módulo completo ─────────────────────────────────────────

const MODULE_DATA = [
  {
    num: "1", id: "dashboard", route: "/dashboard",
    icon: LayoutDashboard, color: "#007934", bg: "#e8f5ee",
    title: "Dashboard",
    subtitle: "Resumen ejecutivo en tiempo real",
    desc: "Página de inicio tras el login. Muestra el estado consolidado de la plataforma: KPIs de proyectos activos, presupuesto ejecutado, sincronización con Azure DevOps y acceso rápido a todos los módulos.",
    features: [
      { icon: "📊", t: "KPIs principales", d: "Proyectos activos, presupuesto ejecutado, recursos y work items sincronizados." },
      { icon: "📈", t: "Ejecución presupuestaria", d: "Gráfico de barras con desglose ejecutado vs. disponible por categoría." },
      { icon: "📁", t: "Proyectos recientes", d: "Acceso rápido a los últimos proyectos modificados con estado y progreso." },
      { icon: "🔄", t: "Estado Azure DevOps", d: "Última sincronización automática (3 veces/día: 8:00, 14:00, 20:00)." },
      { icon: "🔔", t: "Notificaciones", d: "Alertas de plazos próximos, licitaciones calientes y eventos relevantes." },
    ]
  },
  {
    num: "2", id: "tender360", route: "/tender360",
    icon: Search, color: "#0d7377", bg: "#e0f7f8",
    title: "US_BID360",
    subtitle: "Detección automática de licitaciones públicas",
    desc: "Motor de inteligencia de mercado. Detecta licitaciones desde PLACSP (nacional), BOE y Open Data Euskadi (Plataforma Vasca). El ciclo automático diario a las 9:00h filtra por 26 CPVs y más de 55 palabras clave configurables.",
    features: [
      { icon: "🗺️", t: "M0 – Cuadro de Mando", d: "Pipeline ponderado, win rate, forecast trimestral, alertas activas y tasa de conversión Pando." },
      { icon: "🌍", t: "M1 – Market Universe", d: "Todas las licitaciones detectadas con filtros por CPV, provincia, actividad e importe. Selector de columnas configurable." },
      { icon: "📋", t: "M2 – Pipeline & Forecast", d: "Licitaciones en pipeline activo con scoring Go/No-Go y probabilidad de éxito ponderada." },
      { icon: "🤖", t: "Scoring Pando automático", d: "Claude Sonnet 4.6 analiza cada licitación y asigna puntuación GO (0–100) con justificación técnico-comercial." },
      { icon: "🏷️", t: "CPVs y palabras clave", d: "26 CPVs vigilados + 55 keywords base (ES/GL/CA) + keywords personalizadas sin límite desde el panel Admin." },
      { icon: "⚠️", t: "Alertas inteligentes", d: "Plazos inminentes (<30 días), licitaciones de alto valor sin estado y oportunidades recomendadas por la Pando." },
    ]
  },
  {
    num: "3", id: "licitaciones", route: "/licitaciones",
    icon: FileText, color: "#7c3aed", bg: "#ede9fe",
    title: "Licitaciones CRM",
    subtitle: "Pipeline completo de expedientes comerciales",
    desc: "CRM interno para el seguimiento completo del ciclo de vida de cada expediente comercial, desde la detección inicial hasta la adjudicación. Actualmente con 226 expedientes del mercado 2026 importados.",
    features: [
      { icon: "📌", t: "Vista Kanban + Lista", d: "Kanban por estados del pipeline (Prospecto → Recogiendo docs → Analizando → Presentada → Ganada) y tabla compacta con drag & drop." },
      { icon: "➕", t: "Nuevo expediente manual", d: "Crear licitación con todos sus metadatos: expediente, objeto, municipio, importe, responsable, CPV, plazo…" },
      { icon: "📥", t: "Importación masiva Excel", d: "Upsert por número de expediente desde el Excel de Ofertas (actualiza si existe, crea si no)." },
      { icon: "🤖", t: "Análisis Pando de viabilidad", d: "Claude Sonnet 4.6 elabora un informe de viabilidad técnica y comercial con competidores probables y estrategia." },
      { icon: "📄", t: "Generación memoria técnica", d: "Pando genera borradores de la memoria técnica a partir del expediente y documentación adjunta." },
      { icon: "🏆", t: "KPIs del pipeline", d: "Expedientes activos, pipeline total (€), pipeline ponderado (€) y licitaciones ganadas en la cabecera." },
    ]
  },
  {
    num: "4", id: "projects", route: "/projects",
    icon: Folder, color: "#d97706", bg: "#fef3c7",
    title: "Projects & Work Items",
    subtitle: "Gestión de proyectos con Azure DevOps",
    desc: "Gestión de proyectos activos de Valoriza con integración bidireccional con Azure DevOps. Cada proyecto contiene Work Items sincronizados, sprints, backlogs y checklists.",
    features: [
      { icon: "📁", t: "Listado de proyectos", d: "Tabla filtrable por tipo (DOTNET, GIS, HARDWARE, INTEGRATION…), estado y prioridad." },
      { icon: "🔄", t: "Sincronización Azure DevOps", d: "Work Items se sincronizan automáticamente 3 veces/día. Sincronización manual disponible desde el panel DevOps." },
      { icon: "📋", t: "Kanban de Work Items", d: "Tablero Kanban (New → Active → Resolved → Closed) con arrastrar y soltar dentro de cada proyecto." },
      { icon: "📊", t: "Panel PM (Slide-over)", d: "Clic en un Work Item abre un panel lateral con metadatos completos, timeline de notas del equipo e historial de cambios." },
      { icon: "💬", t: "Notas de equipo internas", d: "Comentarios privados por Work Item con avatares y timestamps. No se sincronizan a DevOps." },
      { icon: "⬆️", t: "Publicar en Azure DevOps", d: "Work Items creados localmente se publican en Azure DevOps con el botón 'Enviar a DevOps'." },
    ]
  },
  {
    num: "5", id: "arrive", route: "/arrive",
    icon: Activity, color: "#0284c7", bg: "#e0f2fe",
    title: "Checklists ARRIVE",
    subtitle: "Pruebas de integración APP MobilityHub",
    desc: "Módulo de control de calidad para las pruebas de integración de la APP de movilidad MobilityHub. Cada checklist cubre las funcionalidades de la APP para una ciudad específica.",
    features: [
      { icon: "🏙️", t: "Checklists por ciudad", d: "Un checklist por ciudad con todas las pruebas funcionales. Estados: OK (verde), NOK (rojo), N/A (amarillo)." },
      { icon: "🤖", t: "Importar con Pando – Wizard", d: "Sube un Excel con casos de prueba y el sistema los parsea e importa con confirmación paso a paso." },
      { icon: "⚡", t: "Importar con Pando – Auto", d: "Importación automática sin confirmación para Excels con formato estándar conocido. Más rápida." },
      { icon: "📤", t: "Exportar a Excel", d: "Descarga el estado actual del checklist en formato .xlsx con todos los casos, estados y observaciones." },
      { icon: "📊", t: "Progreso visual", d: "Barra de progreso total y desglose OK/NOK/N/A por ciudad. Indicadores de color en tiempo real." },
    ]
  },
  {
    num: "6", id: "admin", route: "/admin",
    icon: Settings, color: "#dc2626", bg: "#fee2e2",
    title: "Administración",
    subtitle: "Gestión del sistema — solo IT Admin",
    desc: "Panel de administración accesible solo para usuarios con rol IT Admin. Centraliza la gestión de usuarios, configuración del sistema, herramientas de diagnóstico y control de costes de Pando.",
    features: [
      { icon: "👥", t: "Gestión de usuarios", d: "Alta, baja y edición de cuentas. Asignación de roles y permisos por módulo. Reseteo de contraseñas." },
      { icon: "🏷️", t: "Palabras clave de detección", d: "Añadir/eliminar keywords personalizadas para ampliar la detección de licitaciones sin tocar código." },
      { icon: "🔑", t: "Catálogo de CPVs activos", d: "26 CPVs vigilados por el scraper (SERVICIOS + TRATAMIENTO). Ampliable bajo petición." },
      { icon: "🤖", t: "Monitor de uso Pando", d: "Consumo de tokens Claude por usuario y módulo. Control de costes en tiempo real." },
      { icon: "🔄", t: "Sync PLACSP / Euskadi", d: "Disparadores manuales para lanzar sincronización sin esperar al ciclo nocturno." },
      { icon: "📋", t: "Audit Log", d: "Registro completo de acciones: creaciones, modificaciones, exportaciones y accesos a datos sensibles." },
    ]
  },
];

function PlatformManual() {
  const [activeModule, setActiveModule] = useState(null);
  const [expanded, setExpanded] = useState({});

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const handleDownloadPDF = () => {
    const url = `/manual_valoriza_platform.pdf`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "Manual_Valoriza_CorporatePlatforms_2026.pdf";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* PDF download banner */}
      <div className="bg-gradient-to-r from-[#007934] to-[#00a847] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap"
           data-testid="platform-manual-banner">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <BookOpen size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-base font-['Outfit']">Manual de Usuario — Valoriza Corporate Platforms</h2>
            <p className="text-green-100 text-xs mt-0.5">Versión junio 2026 · 7 módulos · Con capturas de pantalla actuales</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleDownloadPDF}
            data-testid="download-platform-pdf-btn"
            className="flex items-center gap-2 bg-white text-[#007934] px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-green-50 transition-all shadow-md"
          >
            <Download size={15} /> Descargar PDF
          </button>
          <a
            href="mailto:dredondo@valorizasm.com?subject=Solicitud soporte plataforma"
            className="flex items-center gap-2 bg-white/20 text-white border border-white/30 px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-white/30 transition-all"
          >
            Soporte IT
          </a>
        </div>
      </div>

      {/* Modules grid */}
      <div className="grid grid-cols-1 gap-3">
        {MODULE_DATA.map((mod) => {
          const Icon = mod.icon;
          const open = expanded[mod.id];
          return (
            <div key={mod.id}
                 data-testid={`manual-module-${mod.id}`}
                 className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-sm transition-shadow">
              {/* Header row */}
              <button
                onClick={() => toggle(mod.id)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: mod.bg }}>
                  <Icon size={16} style={{ color: mod.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: mod.bg, color: mod.color }}>
                      {mod.num}
                    </span>
                    <span className="font-bold text-gray-900 text-sm">{mod.title}</span>
                    <span className="text-gray-400 text-xs hidden sm:inline">— {mod.subtitle}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={mod.route}
                    onClick={e => e.stopPropagation()}
                    className="hidden sm:flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`open-module-${mod.id}`}
                  >
                    <ExternalLink size={11} /> Abrir
                  </a>
                  {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
                </div>
              </button>

              {/* Expanded content */}
              {open && (
                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{mod.desc}</p>
                  <div className="space-y-0 rounded-xl border border-gray-100 overflow-hidden">
                    {mod.features.map((f, i) => (
                      <div key={i} className={`flex items-start gap-3 px-3 py-2.5 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                        <span className="text-base flex-shrink-0 mt-0.5">{f.icon}</span>
                        <div>
                          <span className="text-sm font-semibold text-gray-800">{f.t}</span>
                          <span className="text-xs text-gray-500 ml-2">{f.d}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <a href={mod.route}
                       className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-all hover:opacity-90"
                       style={{ background: mod.color }}
                       data-testid={`go-to-module-${mod.id}`}>
                      <ExternalLink size={11} /> Ir al módulo
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick access cards */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Acceso rápido a módulos</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {MODULE_DATA.map((mod) => {
            const Icon = mod.icon;
            return (
              <a key={mod.id} href={mod.route}
                 data-testid={`quick-access-${mod.id}`}
                 className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-center group">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: mod.bg }}>
                  <Icon size={14} style={{ color: mod.color }} />
                </div>
                <span className="text-xs font-medium text-gray-700 leading-tight">{mod.title.split(" ")[0]}</span>
              </a>
            );
          })}
        </div>
      </div>

      {/* Footer note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start gap-3">
        <CheckCircle size={15} className="text-[#007934] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-0.5">Soporte técnico</p>
          <p className="text-xs text-gray-500">
            Para incidencias o solicitudes de acceso, contacta con <strong>David Redondo</strong> en{" "}
            <a href="mailto:dredondo@valorizasm.com" className="text-[#007934] underline">dredondo@valorizasm.com</a>.
            Las sincronizaciones con Azure DevOps y PLACSP se ejecutan automáticamente cada día a las 9:00h.
          </p>
        </div>
      </div>
    </div>
  );
}



export default function Manuals() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("plataforma");
  const [genBusy, setGenBusy] = useState(false);
  const [genJob, setGenJob] = useState(null);  // {job_id, status, modules_total, captured_count, ...}

  const startManualGeneration = async () => {
    if (!window.confirm(
      "Se generará un PDF integral con capturas reales de cada módulo (~60-120s). " +
      "¿Lanzar generación?"
    )) return;
    setGenBusy(true);
    setGenJob(null);
    try {
      const r = await axios.post(`${API}/api/user-manual/generate`);
      const jid = r.data?.job_id;
      setGenJob({ job_id: jid, status: "queued", modules_total: r.data?.modules_total });
      toast.success("Generación iniciada · capturando módulos…");
      // Poll cada 5s
      const poll = async () => {
        try {
          const s = await axios.get(`${API}/api/user-manual/status/${jid}`);
          setGenJob(s.data);
          if (s.data.status === "done") {
            toast.success(`✓ Manual listo · ${s.data.captured_count}/${s.data.modules_total} módulos capturados`);
            setGenBusy(false);
            return;
          }
          if (s.data.status === "error") {
            toast.error("Error generando manual: " + (s.data.error || "desconocido"));
            setGenBusy(false);
            return;
          }
          setTimeout(poll, 5000);
        } catch (e) {
          toast.error("Error consultando estado");
          setGenBusy(false);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error lanzando generación");
      setGenBusy(false);
    }
  };

  const downloadGeneratedManual = () => {
    if (!genJob?.job_id) return;
    const url = `${API}/api/user-manual/download/${genJob.job_id}`;
    window.open(url, "_blank");
  };

  const TABS = [
    { id: "plataforma",   label: "Plataforma Completa", icon: LayoutDashboard, color: "text-[#007934]" },
    { id: "cfo",          label: "CFO",                  icon: CreditCard,     color: "text-[#007934]" },
    { id: "offers",       label: "Responsable Ofertas",  icon: FileText,       color: "text-teal-700"  },
    { id: "licitaciones", label: "Técnico Licitaciones", icon: FileSpreadsheet, color: "text-purple-700" },
    { id: "admin",        label: "Administrador TIC",    icon: Shield,         color: "text-red-700"   },
  ];

  const PDF_FILES = {
    cfo:          "/manuales/manual_cfo.html",
    offers:       "/manuales/manual_ofertas.html",
    licitaciones: "/manuales/manual_licitaciones.html",
    admin:        "/manuales/manual_admin_tic.html",
  };

  const handleDownloadPDF = () => {
    const url = PDF_FILES[tab];
    if (url) { window.open(url, "_blank"); return; }
    // fallback legacy builder
    const html = buildPDFHtml(tab);
    const blob = new Blob([html], { type: "text/html; charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank");
    if (!win) { alert("El navegador bloqueó la ventana emergente. Permite popups para este sitio."); URL.revokeObjectURL(blobUrl); return; }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };

  return (
    <div className="space-y-5 w-full">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <BookOpen size={22} className="text-[#007934]" /> {t("manuals.title")}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("manuals.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={startManualGeneration}
            disabled={genBusy}
            data-testid="generate-integral-manual-btn"
            title="Genera un PDF nuevo con capturas reales de cada módulo (admin)"
            className="flex items-center gap-2 bg-amber-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 transition-all shadow-sm disabled:opacity-60"
          >
            {genBusy
              ? <><Loader2 size={14} className="animate-spin"/> Generando…</>
              : <><Camera size={14}/> Manual integral con capturas</>}
          </button>
          {genJob?.status === "done" && (
            <button
              onClick={downloadGeneratedManual}
              data-testid="download-generated-manual-btn"
              className="flex items-center gap-2 bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-all shadow-sm">
              <Download size={14}/> Descargar PDF nuevo
            </button>
          )}
          <button
            onClick={() => { const a=document.createElement("a"); a.href="/manual_valoriza_platform.pdf"; a.download="Manual_Valoriza_CorporatePlatforms_2026.pdf"; a.target="_blank"; document.body.appendChild(a); a.click(); document.body.removeChild(a); }}
            className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[#006528] transition-all shadow-sm"
            data-testid="download-full-manual-btn"
          >
            <Download size={14} /> Manual Plataforma 2026 (PDF)
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-all"
            data-testid="print-manual-btn"
          >
            <Printer size={14} /> Imprimir
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 border border-[#007934] text-[#007934] px-3 py-2 rounded-lg text-sm font-semibold hover:bg-green-50 transition-all"
            data-testid="download-pdf-btn"
          >
            <Download size={14} /> HTML perfil
          </button>
        </div>
      </div>

      {/* Banner generación PDF integral */}
      {genJob && (
        <div
          className={`rounded-xl border px-4 py-2.5 text-xs ${
            genJob.status === "done" ? "bg-emerald-50 border-emerald-300 text-emerald-900" :
            genJob.status === "error" ? "bg-rose-50 border-rose-300 text-rose-900" :
            "bg-amber-50 border-amber-300 text-amber-900"
          }`}
          data-testid="manual-job-banner"
        >
          {genJob.status === "queued" && <>📋 Job <span className="font-mono">{genJob.job_id?.slice(0,8)}</span> en cola…</>}
          {genJob.status === "running" && <><Loader2 size={11} className="inline animate-spin mr-1"/> Capturando módulos ({genJob.modules_total || 0})…</>}
          {genJob.status === "done" && <>✓ Manual generado · {genJob.captured_count}/{genJob.modules_total} módulos · {Math.round((genJob.pdf_size||0)/1024)} KB</>}
          {genJob.status === "error" && <>✗ Error: {genJob.error}</>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {TABS.map(({ id, label, icon: Icon, color }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`manual-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === id ? `bg-white shadow-sm ${color}` : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div id="manual-content">
        {tab === "plataforma"   && <PlatformManual />}
        {tab === "cfo"          && <CfoManual />}
        {tab === "offers"       && <OffersManual />}
        {tab === "licitaciones" && (
          <div className="space-y-3">
            <div className="bg-gradient-to-br from-purple-700 to-purple-500 rounded-xl p-5 text-white mb-4">
              <div className="flex items-center gap-3 mb-1">
                <FileSpreadsheet size={20} />
                <h2 className="text-lg font-bold font-['Outfit']">Manual del Técnico de Licitaciones</h2>
              </div>
              <p className="text-purple-100 text-sm">Gestión de expedientes, análisis Pando de pliegos, cobertura VSM y generación de memorias técnicas</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center space-y-3">
              <p className="text-sm text-purple-900">Este manual cubre el módulo completo de Licitaciones (CRM + Pando).</p>
              <a href="/manuales/manual_licitaciones.html" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-purple-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-purple-800 transition-all">
                <Download size={14} /> Ver manual completo (imprimible a PDF)
              </a>
            </div>
          </div>
        )}
        {tab === "admin" && (
          <div className="space-y-3">
            <div className="bg-gradient-to-br from-red-700 to-red-500 rounded-xl p-5 text-white mb-4">
              <div className="flex items-center gap-3 mb-1">
                <Shield size={20} />
                <h2 className="text-lg font-bold font-['Outfit']">Manual del Administrador TIC</h2>
              </div>
              <p className="text-red-100 text-sm">Gestión de usuarios, proyectos, catálogo TIC, ORA Zaragoza, integraciones y despliegue</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center space-y-3">
              <p className="text-sm text-red-900">Este manual cubre la administración completa de la plataforma.</p>
              <a href="/manuales/manual_admin_tic.html" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-red-800 transition-all">
                <Download size={14} /> Ver manual completo (imprimible a PDF)
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
